import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Box,
  Grid,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography
} from '@mui/material';

// Импорты наших модулей
import {
  Document,
  User,
  Storage,
  Pharmacy,
  DocumentType,
  DocumentStatus,
  DocumentDirection,
  SortBy,
  SortOrder,
  DocumentFormData
} from './types';
import {
  useUsers,
  useStorages,
  useDocuments,
  useMaterialSearch,
  useDocumentForm
} from './hooks';
import {
  getDocumentsStats,
  getAllFilteredDocuments,
  createEmptyRow,
  getDocumentNumber,
  calculateMarkCodesCount
} from './utils';
import DocumentList from './DocumentList';
import DocumentFilters from './DocumentFilters';
import DocumentDialog from './DocumentDialog';
import DocumentViewDialog from './DocumentViewDialog';
import DocumentActions from './DocumentActions';
import PushNotificationManager from './PushNotificationManager';
import ReceivingConfirmationDialog from './ReceivingConfirmationDialog';
import CapsLockOverlay from './components/CapsLockOverlay';
import { mergeDuplicateRows } from './mergeTableRows';
import { notify } from '../../notifications';

const Documents: React.FC = () => {
  // Ref для предотвращения дублирования инициализации в React.StrictMode
  const initializationRef = useRef(false);

  // Данные пользователя
  const [username, setUsername] = useState<string>('');
  const [pharmacyNum, setPharmacyNum] = useState<string>('');
  const [guid, setGuid] = useState<string>('');
  const [senderStorage, setSenderStorage] = useState<string>('');

  // Состояния для диалогов
  const [openDialog, setOpenDialog] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [receivingDialogOpen, setReceivingDialogOpen] = useState(false);
  const [receivingDocument, setReceivingDocument] = useState<Document | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingDocumentId, setEditingDocumentId] = useState<string | null>(null);
  const [editingDocumentNumber, setEditingDocumentNumber] = useState<number | undefined>(undefined);
  const [editingDocumentStatus, setEditingDocumentStatus] = useState<number | null>(null);
  const [editingDocumentFull, setEditingDocumentFull] = useState<any>(null); // Полный объект редактируемого документа
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [highlightedDocumentId, setHighlightedDocumentId] = useState<string | null>(null);

  // Состояния для формы документа
  const [documentType, setDocumentType] = useState<number>(4);
  const [toPharmacy, setToPharmacy] = useState<Pharmacy | null>(null);
  const [pharmacySearchInput, setPharmacySearchInput] = useState<string>('');
  const [filteredPharmacies, setFilteredPharmacies] = useState<User[]>([]);
  const [toStorage, setToStorage] = useState<Storage | null>(null);
  const [storageSearchInput, setStorageSearchInput] = useState<string>('');
  const [filteredStorages, setFilteredStorages] = useState<Storage[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);
  const [throughRC, setThroughRC] = useState<boolean | null>(null);
  const [boxCount, setBoxCount] = useState<number>(1);
  const [comment, setComment] = useState<string>('');
  const [isMarkedProduct, setIsMarkedProduct] = useState<boolean>(false);
  const [pendingPharmacyNum, setPendingPharmacyNum] = useState<string | null>(null); // Для отложенной загрузки аптеки
  const [pendingStorageInfo, setPendingStorageInfo] = useState<{ guid?: string | null; name?: string | null } | null>(null); // Для отложенной загрузки склада
  const skipAutosaveRestoreRef = useRef(false); // REF для пропуска восстановления автосохранения (синхронное обновление)
  const editingPharmacyResetRef = useRef(false); // REF: не сбрасывать получателя при открытии на редактирование


  // Состояния для фильтрации и сортировки
  const [filterType, setFilterType] = useState<DocumentType>('all');
  const [filterStatus, setFilterStatus] = useState<DocumentStatus>('all');
  const [filterDirection, setFilterDirection] = useState<DocumentDirection>('all');
  const [sortBy, setSortBy] = useState<SortBy>('id');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [documentSearch, setDocumentSearch] = useState('');

  // Пагинация удалена - используется виртуализация списка

  // Состояния для меню действий
  const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | null>(null);
  const [selectedDocumentForMenu, setSelectedDocumentForMenu] = useState<Document | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Диалог подтверждения смены типа документа
  const [changeTypeDialogOpen, setChangeTypeDialogOpen] = useState(false);
  const [pendingDocumentType, setPendingDocumentType] = useState<number | null>(null);

  // Хуки для работы с данными
  const { users, fetchUsers } = useUsers();
  const { storages, fetchStorages } = useStorages();
  const {
    documents,
    incomingDocuments,
    isLoading,
    fetchDocuments,
    fetchIncomingDocuments,
    fetchDocument,
    saveDocument,
    deleteDocument,
    reloadDocuments
  } = useDocuments(username, guid);
  const { materials, searchMaterials } = useMaterialSearch(guid);
  const {
    tableData,
    materialSearchInputs,
    addTableRow,
    removeTableRow,
    updateTableRow,
    handleMaterialSelect,
    handleMaterialSearchChange,
    resetForm,
    loadDocumentData,
    setTableData,
    setMaterialSearchInputs
  } = useDocumentForm();

  // Инициализация при монтировании компонента
  useEffect(() => {
    const initializeData = async () => {
      if (initializationRef.current) {
        return;
      }

      initializationRef.current = true;
      document.title = 'Документы | Zerde';

      // Данные пользователя из localStorage
      const storedUsername = localStorage.getItem('username') || '';
      const storedPharmacyNum = localStorage.getItem('pharmacyNum') || '';
      const storedGuid = localStorage.getItem('guid') || '';

      setUsername(storedUsername);
      setPharmacyNum(storedPharmacyNum);
      setGuid(storedGuid);

      try {
        // При инициализации загружаем аптеки с фильтром по cityId (throughRC по умолчанию false)
        const currentCityId = localStorage.getItem('cityId') || undefined;
        const usersResult = await fetchUsers(currentCityId);
        if (!usersResult.success) {
          notify.error(usersResult.message || 'Ошибка загрузки пользователей');
        }

      } catch (error) {
        console.error('Ошибка инициализации:', error);
        notify.error('Ошибка инициализации');
      }
    };

    initializeData();
  }, []);

  // Обновляем senderStorage после загрузки users
  useEffect(() => {
    if (username) {
      // Сначала пробуем localStorage (если пользователь залогинился)
      const storedSenderStorage = localStorage.getItem('belongsStorage') || '';
      // console.log(' senderStorage check:', {
      //   username,
      //   storedSenderStorage,
      //   usersCount: users.length
      // });

      if (storedSenderStorage) {
        // console.log(' Установлен senderStorage из localStorage:', storedSenderStorage);
        setSenderStorage(storedSenderStorage);
      } else if (users.length > 0) {
        // Если в localStorage нет, ищем в users (для пользователей, которые не текущий)
        const currentUser = users.find(u => u.username === username);
        //console.log(' Поиск в users:', { currentUser });
        if (currentUser && currentUser.belongsStorage) {
          //console.log(' Установлен senderStorage из users:', currentUser.belongsStorage);
          setSenderStorage(currentUser.belongsStorage);
        } else {
          console.warn('senderStorage не найден ни в localStorage, ни в users');
        }
      }
    }
  }, [users, username]);

  // Загрузка документов при изменении пользователя
  useEffect(() => {
    if (username && guid) {
      reloadDocuments();
    }
  }, [username, guid, reloadDocuments]);

  // Отложенная загрузка аптеки когда users загрузятся
  useEffect(() => {
    if (pendingPharmacyNum && users.length > 0 && openDialog) {
      // console.log(' Попытка загрузить отложенную аптеку:', {
      //   pendingPharmacyNum,
      //   usersCount: users.length
      // });

      const targetPharmacy = users.find(u => {
        const uNum = String(u.pharmacyNum).trim();
        return uNum === pendingPharmacyNum;
      });

      if (targetPharmacy) {
        //console.log(' Отложенная аптека найдена:', targetPharmacy);
        setToPharmacy({
          pharmacyNum: targetPharmacy.pharmacyNum,
          guid: targetPharmacy.guid,
          username: targetPharmacy.username,
          belongsStorage: targetPharmacy.belongsStorage,
          cityId: targetPharmacy.cityId
        });
        setPharmacySearchInput(`Аптека №${targetPharmacy.pharmacyNum}`);
        setPendingPharmacyNum(null); // Сбрасываем pending после успешной загрузки
      } else {
        console.warn('Отложенная аптека не найдена:', pendingPharmacyNum);
      }
    }
  }, [users, pendingPharmacyNum, openDialog]);

  // Отложенная загрузка склада когда storages загрузятся (для редактирования документа типа 4)
  useEffect(() => {
    if (pendingStorageInfo && openDialog && documentType === 4) {
      if (storages.length > 0) {
        const targetStorage = storages.find(s => {
          const matchGuid = pendingStorageInfo.guid && s.guid && s.guid.toLowerCase() === pendingStorageInfo.guid.toLowerCase();
          const matchUsername = pendingStorageInfo.name && s.username && s.username.toLowerCase() === pendingStorageInfo.name.toLowerCase();
          const matchPharmacyNum = pendingStorageInfo.name && s.pharmacyNum && s.pharmacyNum.toLowerCase() === pendingStorageInfo.name.toLowerCase();
          return matchGuid || matchUsername || matchPharmacyNum;
        });

        if (targetStorage) {
          setToStorage(targetStorage);
          setStorageSearchInput(targetStorage.username);
          setPendingStorageInfo(null);
        } else {
          // Если склад не найден в списке (например, архивирован/деактивирован), используем fallback
          const fallbackStorage: Storage = {
            id: 0,
            username: pendingStorageInfo.name || 'Склад',
            pharmacyNum: pendingStorageInfo.name || '',
            guid: pendingStorageInfo.guid || ''
          };
          setToStorage(fallbackStorage);
          setStorageSearchInput(fallbackStorage.username);
          setPendingStorageInfo(null);
        }
      }
    }
  }, [storages, pendingStorageInfo, openDialog, documentType]);

  // Загрузка складов при открытии диалога с типом 4 (возврат на склад), если список еще пуст
  useEffect(() => {
    if (openDialog && documentType === 4 && storages.length === 0) {
      fetchStorages();
    }
  }, [openDialog, documentType, storages.length, fetchStorages]);

  // Автообновление списка документов каждые 5 минут, если нет открытых диалогов
  useEffect(() => {
    // Проверяем, что нет открытых диалогов
    const isIdle = !openDialog && !viewDialogOpen && !receivingDialogOpen;

    if (!isIdle || !username || !guid) {
      return; // Не запускаем таймер если есть открытые диалоги или пользователь не авторизован
    }

    const intervalId = setInterval(() => {
      //console.log('Автообновление списка документов');
      reloadDocuments();
    }, 5.1 * 60 * 1000); // 5 минут 10 сек

    return () => clearInterval(intervalId);
  }, [openDialog, viewDialogOpen, receivingDialogOpen, username, guid, reloadDocuments]);

  // Перезагрузка списка аптек при изменении throughRC
  useEffect(() => {
    if (!openDialog) return; // Только когда диалог открыт
    if (documentType === 4) return; // Для возвратов (склад) аптеки не нужны

    // При открытии на редактирование - не сбрасываем получателя и грузим ВСЕ аптеки
    if (editingPharmacyResetRef.current) {
      editingPharmacyResetRef.current = false;
      // Для редактирования загружаем полный список (без фильтра),
      // чтобы pendingPharmacyNum нашёл аптеку даже из другого региона
      fetchUsers();
      return;
    }

    const currentCityId = localStorage.getItem('cityId') || undefined;
    // throughRC = false (прямое) → фильтруем по cityId, throughRC = true (через РЦ) → все аптеки
    const cityIdParam = throughRC ? undefined : currentCityId;

    // Сбрасываем выбранного получателя при смене throughRC
    setToPharmacy(null);
    setPharmacySearchInput('');
    setFilteredPharmacies([]);

    fetchUsers(cityIdParam);
  }, [throughRC, openDialog, documentType]);


  // Пагинация удалена - фильтры работают напрямую с виртуализированным списком

  // Функции для работы с диалогами
  const handleCreateDocument = () => {
    setOpenDialog(true);
    setDocumentType(5);
    setToPharmacy(null);
    setPharmacySearchInput('');
    setFilteredPharmacies([]);
    setToStorage(null);
    setStorageSearchInput('');
    setFilteredStorages([]);
    setTableData([createEmptyRow()]);
    setIsEditMode(false);
    setEditingDocumentId(null);
    setEditingDocumentFull(null);
    setEditingDocumentNumber(undefined);
    setEditingDocumentStatus(null);
    setThroughRC(false);
    setBoxCount(0);
    setComment('');
    setIsMarkedProduct(false);
    setPendingPharmacyNum(null); // Сбрасываем отложенную аптеку
    setPendingStorageInfo(null);
    fetchStorages(); // Загружаем список складов
  };

  // Закрытие через клик на оверлей (backdrop) - НЕ очищаем автосохранение для новых документов
  const handleBackdropClick = () => {
    setOpenDialog(false);
    // Не очищаем данные формы для новых документов - пусть автосохранение работает
    if (isEditMode) {
      // Для редактирования - очищаем всё
      resetForm();
      setPharmacySearchInput('');
      setFilteredPharmacies([]);
      setToStorage(null);
      setStorageSearchInput('');
      setFilteredStorages([]);
      setPendingStorageInfo(null);
      setIsEditMode(false);
      setEditingDocumentId(null);
      setEditingDocumentFull(null);
      setEditingDocumentNumber(undefined);
      setEditingDocumentStatus(null);
      setThroughRC(false);
      setBoxCount(0);
      setComment('');
      setPendingPharmacyNum(null);
    }
    setIsSaving(false);
  };

  // Закрытие через кнопку "Отмена" - очищаем всё включая автосохранение
  const handleCancelClick = () => {
    setOpenDialog(false);
    resetForm();
    setPharmacySearchInput('');
    setFilteredPharmacies([]);
    setToStorage(null);
    setStorageSearchInput('');
    setFilteredStorages([]);
    setPendingStorageInfo(null);
    setIsEditMode(false);
    setEditingDocumentId(null);
    setEditingDocumentFull(null);
    setEditingDocumentNumber(undefined);
    setEditingDocumentStatus(null);
    setIsSaving(false);
    setThroughRC(false);
    setIsMarkedProduct(false);
    setBoxCount(0);
    setComment('');
    setPendingPharmacyNum(null);
    // Очищаем автосохранение
    localStorage.removeItem(AUTOSAVE_KEY);
  };

  // Совместимость - для случаев когда нужно закрыть с очисткой
  const handleCloseDialog = handleCancelClick;

  // Функции для работы с аптеками
  const handlePharmacySearchChange = (value: string) => {
    setPharmacySearchInput(value);

    if (!value.trim()) {
      setFilteredPharmacies([]);
      return;
    }

    // Фильтрация аптек на фронтенде
    const availablePharmacies = users
      .filter(user => user.pharmacyNum !== pharmacyNum)
      .filter((user, index, self) =>
        index === self.findIndex(u => u.pharmacyNum === user.pharmacyNum)
      );

    const filtered = availablePharmacies.filter(user => {
      const pharmacyName = `Аптека №${user.pharmacyNum}`;
      const fullName = user.fullName || '';

      return pharmacyName.toLowerCase().includes(value.toLowerCase()) ||
        fullName.toLowerCase().includes(value.toLowerCase()) ||
        user.pharmacyNum.includes(value);
    });

    setFilteredPharmacies(filtered);
  };

  const handlePharmacySelect = (user: User | null) => {
    if (user) {
      setToPharmacy({
        pharmacyNum: user.pharmacyNum,
        guid: user.guid,
        username: user.username,
        belongsStorage: user.belongsStorage,
        cityId: user.cityId
      });
      setPharmacySearchInput(`Аптека №${user.pharmacyNum}`);
    } else {
      setToPharmacy(null);
      setPharmacySearchInput('');
    }
    setFilteredPharmacies([]);
  };

  // Поиск складов
  const handleStorageSearchChange = (value: string) => {
    setStorageSearchInput(value);

    if (!value.trim()) {
      setFilteredStorages([]);
      return;
    }

    const filtered = storages.filter(storage => {
      return storage.username.toLowerCase().includes(value.toLowerCase()) ||
        (storage.cityId && storage.cityId.toLowerCase().includes(value.toLowerCase()));
    });

    setFilteredStorages(filtered);
  };

  const handleStorageSelect = (storage: Storage | null) => {
    if (storage) {
      setToStorage(storage);
      setStorageSearchInput(storage.username);
    } else {
      setToStorage(null);
      setStorageSearchInput('');
    }
    setFilteredStorages([]);
  };

  // Обработка поиска материалов
  const handleMaterialSearchChangeWrapper = (rowId: string, value: string) => {
    handleMaterialSearchChange(rowId, value);

    if (value.length >= 2) {
      searchMaterials(value, documentType);
    }
  };

  // Автосохранение для новых документов
  const AUTOSAVE_KEY = 'new_document_autosave';

  // Восстановление из автосохранения при открытии диалога создания
  useEffect(() => {
    // Пропускаем восстановление если установлен флаг (например, при создании на основании)
    if (skipAutosaveRestoreRef.current) {
      skipAutosaveRestoreRef.current = false; // Сбрасываем флаг
      return;
    }

    if (openDialog && !isEditMode) {
      try {
        const savedData = localStorage.getItem(AUTOSAVE_KEY);
        if (savedData) {
          const parsed = JSON.parse(savedData);
          const savedTime = new Date(parsed.timestamp).getTime();
          const now = new Date().getTime();

          // Если сохранению меньше 10 минут
          if (now - savedTime < 10 * 60 * 1000) {
            // console.log('Восстановление черновика:', parsed);

            if (parsed.documentType) setDocumentType(parsed.documentType);
            if (parsed.isMarkedProduct !== undefined) setIsMarkedProduct(parsed.isMarkedProduct);
            if (parsed.toPharmacy) {
              setToPharmacy(parsed.toPharmacy);
              setPharmacySearchInput(`Аптека №${parsed.toPharmacy.pharmacyNum}`);
            }
            if (parsed.toStorage) {
              setToStorage(parsed.toStorage);
              setStorageSearchInput(parsed.toStorage.username || '');
            }
            if (parsed.throughRC !== undefined) setThroughRC(parsed.throughRC);
            if (parsed.boxCount) setBoxCount(parsed.boxCount);
            if (parsed.comment) setComment(parsed.comment);
            if (parsed.tableData && Array.isArray(parsed.tableData)) {
              // Восстанавливаем tableData с правильной структурой
              const restoredTableData = parsed.tableData.map((row: any) => ({
                id: row.id || (Date.now().toString() + Math.random()),
                MaterialZid: row.MaterialZid || '',
                MaterialCode: row.MaterialCode || '',
                MaterialName: row.MaterialName || '',
                SerialNumber: row.SerialNumber || '',
                SerialDate: row.SerialDate || '',
                Quantity: row.Quantity || '',
                QuantityPcs: row.QuantityPcs || '', // Количество в штуках
                Quantity1C: row.Quantity1C || '',
                Barcode: row.Barcode || '',
                Comment: row.Comment || '',
                // Восстанавливаем series
                series: typeof row.series === 'string' ? row.series : JSON.stringify(row.series || []),
                // Восстанавливаем markCodes
                markCodes: typeof row.markCodes === 'string'
                  ? JSON.parse(row.markCodes)
                  : (Array.isArray(row.markCodes) ? row.markCodes : []),
                partialScanning: row.partialScanning || false,
                // Восстанавливаем selectedMaterial если есть данные
                selectedMaterial: row.MaterialName ? {
                  ProductZid: row.MaterialZid || '',
                  ProductSku: row.MaterialCode || '',
                  ProductName: row.MaterialName,
                  Barcode: row.Barcode || '',
                  PartExists: true, // Для восстановленных данных
                  PartCoeff: 0
                } : undefined
              }));
              setTableData(restoredTableData);
            }

            notify.info('Черновик восстановлен');
          } else {
            localStorage.removeItem(AUTOSAVE_KEY);
          }
        }
      } catch (e) {
        console.error('Ошибка восстановления черновика:', e);
      }
    }
  }, [openDialog, isEditMode]);

  // Сохранение состояния в localStorage
  useEffect(() => {
    if (openDialog && !isEditMode && !isSaving) {
      const timer = setTimeout(() => {
        // Очищаем tableData от selectedMaterial чтобы избежать циклических ссылок
        const cleanTableData = tableData.map(row => ({
          id: row.id,
          MaterialZid: row.MaterialZid,
          MaterialCode: row.MaterialCode,
          MaterialName: row.MaterialName,
          SerialNumber: row.SerialNumber,
          SerialDate: row.SerialDate,
          Quantity: row.Quantity,
          QuantityPcs: row.QuantityPcs || '', // Количество в штуках
          Quantity1C: row.Quantity1C || '',
          Barcode: row.Barcode,
          Comment: row.Comment || '',
          markCodes: Array.isArray(row.markCodes) ? row.markCodes : [],
          partialScanning: row.partialScanning,
          series: row.series
        }));

        const dataToSave = {
          timestamp: new Date(),
          documentType,
          toPharmacy,
          toStorage,
          throughRC,
          boxCount,
          comment,
          isMarkedProduct,
          tableData: cleanTableData
        };

        try {
          localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(dataToSave));
          // console.log('Автосохранение выполнено:', {
          //   товаров: cleanTableData.length,
          //   timestamp: dataToSave.timestamp
          // });
        } catch (e) {
          console.error('Ошибка автосохранения:', e);
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [openDialog, isEditMode, isSaving, documentType, toPharmacy, toStorage, throughRC, boxCount, comment, isMarkedProduct, tableData]);

  // Очистка автосохранения при успешном сохранении
  const clearAutosave = () => {
    localStorage.removeItem(AUTOSAVE_KEY);
  };

  // Сохранение документа
  const handleSaveDocument = async (status: number = 1) => {
    if (isSaving) return;

    // 1. Проверка получателя
    if (documentType === 5 && !toPharmacy) {
      notify.error('Выберите аптеку получателя');
      return;
    }
    if (documentType === 4 && !toStorage) {
      notify.error('Выберите склад получателя');
      return;
    }

    // 2. Отфильтровываем пустые строки и проверяем товары
    const validRows = tableData.filter(row =>
      Boolean(row.selectedMaterial || row.MaterialCode || row.MaterialName ||
        (row.Quantity && parseFloat(row.Quantity) > 0) ||
        (row.QuantityPcs && parseFloat(row.QuantityPcs) > 0))
    );

    if (validRows.length === 0) {
      notify.error('Добавьте хотя бы один товар в документ');
      return;
    }

    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i];
      if (!row.selectedMaterial && !row.MaterialCode) {
        notify.error(`Товар №${i + 1}: выберите товар из справочника`);
        return;
      }
      const hasQty = (row.Quantity && parseFloat(row.Quantity) > 0) || (row.QuantityPcs && parseFloat(row.QuantityPcs) > 0);
      if (!hasQty) {
        notify.error(`Товар №${i + 1}: укажите количество`);
        return;
      }
    }

    // 3. Строгая проверка - применяется и для черновика (status=9), и для отправки в работу
    if (documentType === 5 && throughRC === null) {
      notify.error('Укажите значение «Через РЦ»');
      return;
    }

    if (boxCount <= 0) {
      notify.error('Укажите количество коробов (больше 0)');
      return;
    }

    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i];
      const stockMax = row.selectedMaterial?.StockCount;
      const qty = parseFloat(row.Quantity || '0');
      if (stockMax !== undefined && qty > stockMax) {
        notify.error(`Товар №${i + 1}: количество (${qty} упак) превышает остаток (${stockMax} упак)`);
        return;
      }

      const neededCodes = calculateMarkCodesCount(row.Quantity, row.QuantityPcs, row.selectedMaterial?.PartCoeff);
      const markCodes = row.markCodes ? (Array.isArray(row.markCodes) ? row.markCodes : JSON.parse(row.markCodes || '[]')) : [];
      const filledCodes = markCodes.filter((c: string) => c && c.length === 31);
      const invalidCodes = markCodes.filter((c: string) => c && c.length > 0 && c.length !== 31);

      if (invalidCodes.length > 0) {
        notify.error(`Товар №${i + 1}: ${invalidCodes.length} код(ов) имеют неверную длину (должно быть 31 символ)`);
        return;
      }

      const isStrict = documentType === 4 && isMarkedProduct;
      if ((isStrict || !row.partialScanning) && filledCodes.length < neededCodes) {
        notify.error(`Товар №${i + 1}: не все коды маркировки отсканированы (${filledCodes.length} из ${neededCodes})`);
        return;
      }

      // Для возврата (тип 4) - серии
      if (documentType === 4) {
        const qtyCeil = Math.ceil(parseFloat(row.Quantity || '0') || 0);
        const seriesArr: any[] = row.series
          ? (() => { try { return JSON.parse(row.series); } catch { return []; } })()
          : [];
        const filledSeries = seriesArr.filter(s => {
          const name = typeof s === 'string' ? s : (s?.name || '');
          return name && name.trim();
        }).length;
        if (qtyCeil > 0 && filledSeries !== qtyCeil) {
          if (filledSeries < qtyCeil) {
            notify.error(`Товар №${i + 1}: заполнено ${filledSeries}/${qtyCeil} серий`);
          } else {
            notify.error(`Товар №${i + 1}: количество в сериях (${filledSeries}) превышает количество товара (${qtyCeil})`);
          }
          return;
        }
      }
    }

    // Проверка на дубликаты кодов маркировки по всему документу
    const allCodes: string[] = [];
    for (const row of validRows) {
      const codes = row.markCodes ? (Array.isArray(row.markCodes) ? row.markCodes : JSON.parse(row.markCodes || '[]')) : [];
      for (const c of codes) {
        if (c && c.length === 31) {
          if (allCodes.includes(c)) {
            notify.error(`Обнаружен дублирующийся код маркировки: ...${c.slice(-8)}`);
            return;
          }
          allCodes.push(c);
        }
      }
    }

    setIsSaving(true);

    try {
      const rowsToProcess = validRows;

      // Объединяем дублирующиеся строки перед сохранением
      const mergedRows = mergeDuplicateRows(rowsToProcess);
      if (mergedRows.length !== rowsToProcess.length) {
        setTableData(mergedRows);
        notify.info(`Объединено дублей: ${rowsToProcess.length} → ${mergedRows.length} позиций`);
      }

      const documentData: DocumentFormData = {
        type: documentType,
        fromPharmacy: pharmacyNum,
        toPharmacy: documentType === 5 ? toPharmacy : (documentType === 4 && toStorage ? { pharmacyNum: toStorage.pharmacyNum || toStorage.username, guid: toStorage.guid, username: toStorage.username, belongsStorage: toStorage.belongsStorage, cityId: toStorage.cityId } : null),
        username: username,
        guid: guid,
        status: status,
        throughRC: documentType === 5 ? (throughRC ?? undefined) : undefined,
        senderStorage: documentType === 5 && throughRC ? senderStorage : undefined,
        boxCount: boxCount > 0 ? boxCount : undefined,
        comment: comment || undefined,
        isMarkedProduct: documentType === 4 ? isMarkedProduct : undefined,
        requestMarks: documentType === 4 ? (isMarkedProduct ? 1 : 0) : null,
        tableData: mergedRows.map(row => ({
          id: row.id,
          MaterialZid: row.MaterialZid,
          MaterialCode: row.MaterialCode,
          MaterialName: row.MaterialName,
          SerialNumber: row.SerialNumber,
          SerialDate: row.SerialDate,
          Quantity: row.Quantity,
          QuantityPcs: row.QuantityPcs || '', // Количество в штуках
          Quantity1C: row.Quantity1C || '',
          Barcode: row.Barcode,
          Comment: row.Comment || '',
          markCodes: row.markCodes ? (Array.isArray(row.markCodes) ? row.markCodes : JSON.parse(row.markCodes)) : [],
          series: row.series
        }))
      };

      const result = await saveDocument(documentData, isEditMode, editingDocumentId);

      if (result.success) {
        notify.success(result.message || (status === 9 ? 'Черновик успешно сохранен' : 'Документ успешно отправлен в работу'));
        clearAutosave(); // Очищаем автосохранение
        await reloadDocuments();

        // Подсвечиваем новый документ если он был создан (статус 1 или 7, не черновик)
        if (status !== 9 && result.documentId) {
          setHighlightedDocumentId(result.documentId);
          // Убираем подсветку через 5 секунд
          setTimeout(() => setHighlightedDocumentId(null), 5000);
        }

        handleCloseDialog();
      } else {
        const errorMsg = typeof result.message === 'string' ? result.message : 'Ошибка при сохранении документа';
        notify.error(errorMsg);
      }
    } catch (error: any) {
      console.error('Ошибка при сохранении документа:', error);
      const serverMsg = error?.response?.data?.message || error?.message || 'Ошибка при сохранении документа';
      notify.error(serverMsg);
    } finally {
      setIsSaving(false);
    }
  };

  // Сохранить документ как "Сканирование коробов" и вернуть documentId
  const handleSaveAsDraft = async (): Promise<string | null> => {
    if (isSaving) return null;

    if (documentType === 5 && !toPharmacy) {
      notify.error('Выберите аптеку получателя');
      return null;
    }
    if (documentType === 4 && !toStorage) {
      notify.error('Выберите склад получателя');
      return null;
    }
    if (documentType === 5 && throughRC === null) {
      notify.error('Укажите значение «Через РЦ»');
      return null;
    }
    if (boxCount <= 0) {
      notify.error('Укажите количество коробов (больше 0)');
      return null;
    }

    const validRows = tableData.filter(row =>
      Boolean(row.selectedMaterial || row.MaterialCode || row.MaterialName ||
        (row.Quantity && parseFloat(row.Quantity) > 0) ||
        (row.QuantityPcs && parseFloat(row.QuantityPcs) > 0))
    );

    if (validRows.length === 0) {
      notify.error('Добавьте хотя бы один товар в документ');
      return null;
    }

    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i];
      if (!row.selectedMaterial && !row.MaterialCode) {
        notify.error(`Товар №${i + 1}: выберите товар из справочника`);
        return null;
      }
      const hasQty = (row.Quantity && parseFloat(row.Quantity) > 0) || (row.QuantityPcs && parseFloat(row.QuantityPcs) > 0);
      if (!hasQty) {
        notify.error(`Товар №${i + 1}: укажите количество`);
        return null;
      }

      const stockMax = row.selectedMaterial?.StockCount;
      const qty = parseFloat(row.Quantity || '0');
      if (stockMax !== undefined && qty > stockMax) {
        notify.error(`Товар №${i + 1}: количество (${qty} упак) превышает остаток (${stockMax} упак)`);
        return null;
      }

      const neededCodes = calculateMarkCodesCount(row.Quantity, row.QuantityPcs, row.selectedMaterial?.PartCoeff);
      const markCodes = row.markCodes ? (Array.isArray(row.markCodes) ? row.markCodes : JSON.parse(row.markCodes || '[]')) : [];
      const filledCodes = markCodes.filter((c: string) => c && c.length === 31);
      const invalidCodes = markCodes.filter((c: string) => c && c.length > 0 && c.length !== 31);

      if (invalidCodes.length > 0) {
        notify.error(`Товар №${i + 1}: ${invalidCodes.length} код(ов) имеют неверную длину (должно быть 31 символ)`);
        return null;
      }

      const isStrict = documentType === 4 && isMarkedProduct;
      if ((isStrict || !row.partialScanning) && filledCodes.length < neededCodes) {
        notify.error(`Товар №${i + 1}: не все коды маркировки отсканированы (${filledCodes.length} из ${neededCodes})`);
        return null;
      }
    }

    setIsSaving(true);

    try {
      const rowsToProcess = validRows;

      // Объединяем дублирующиеся строки перед сохранением
      const mergedRows = mergeDuplicateRows(rowsToProcess);
      if (mergedRows.length !== rowsToProcess.length) {
        setTableData(mergedRows);
        notify.info(`Объединено дублей: ${rowsToProcess.length} → ${mergedRows.length} позиций`);
      }

      const documentData: DocumentFormData = {
        type: documentType,
        fromPharmacy: pharmacyNum,
        toPharmacy: documentType === 5 ? toPharmacy : (documentType === 4 && toStorage ? { pharmacyNum: toStorage.pharmacyNum || toStorage.username, guid: toStorage.guid, username: toStorage.username, belongsStorage: toStorage.belongsStorage, cityId: toStorage.cityId } : null),
        username: username,
        guid: guid,
        status: 12, // Сканирование коробов (было 9 - Черновик)
        throughRC: documentType === 5 ? (throughRC ?? undefined) : undefined,
        senderStorage: documentType === 5 && throughRC ? senderStorage : undefined,
        boxCount: boxCount > 0 ? boxCount : undefined,
        comment: comment || undefined,
        isMarkedProduct: documentType === 4 ? isMarkedProduct : undefined,
        requestMarks: documentType === 4 ? (isMarkedProduct ? 1 : 0) : null,
        tableData: mergedRows.map(row => ({
          id: row.id,
          MaterialZid: row.MaterialZid,
          MaterialCode: row.MaterialCode,
          MaterialName: row.MaterialName,
          SerialNumber: row.SerialNumber,
          SerialDate: row.SerialDate,
          Quantity: row.Quantity,
          QuantityPcs: row.QuantityPcs || '', // Количество в штуках
          Quantity1C: row.Quantity1C || '',
          Barcode: row.Barcode,
          Comment: row.Comment || '',
          markCodes: row.markCodes ? (Array.isArray(row.markCodes) ? row.markCodes : JSON.parse(row.markCodes)) : [],
          series: row.series
        }))
      };

      const result = await saveDocument(documentData, isEditMode, editingDocumentId);

      if (result.success && result.documentId) {
        notify.success('Документ отправлен на сканирование коробов');

        // Обновляем состояние документа
        setEditingDocumentId(result.documentId);
        setEditingDocumentNumber(parseInt(result.documentId));
        setIsEditMode(true);

        return result.documentId;
      } else {
        const errorMsg = typeof result.message === 'string' ? result.message : 'Ошибка при сохранении документа';
        notify.error(errorMsg);
        return null;
      }
    } catch (error: any) {
      console.error('Ошибка при сохранении документа:', error);
      const serverMsg = error?.response?.data?.message || error?.message || 'Ошибка при сохранении документа';
      notify.error(serverMsg);
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  // Завершить сканирование коробов и отправить в работу
  const handleCompleteWithBoxes = async (documentId: string, boxes: any[]) => {
    if (isSaving) return;

    setIsSaving(true);

    try {
      // Отправляем короба на сервер
      const response = await axios.post(`https://tbl.zerdegroup.kz/api/v1/documents/${documentId}/boxes`, {
        boxes: boxes.map(box => ({
          box_num: box.box_num,
          box_code: box.box_code
        })),
        username
      });

      if (response.data.success) {
        notify.success('Документ успешно отправлен в работу!');
        await reloadDocuments();
        handleCloseDialog();
      } else {
        notify.error(response.data.message || 'Ошибка при сохранении коробов');
      }
    } catch (error) {
      console.error('Ошибка при сохранении коробов:', error);
      notify.error('Ошибка при сохранении коробов');
    } finally {
      setIsSaving(false);
    }
  };

  // Редактирование документа (ДОЛЖНА БЫТЬ ПЕРЕД handleViewDocument, т.к. используется в ней)
  const handleEditDocument = (document: Document) => {
    setIsEditMode(true);
    setEditingDocumentId(document.id);
    setEditingDocumentFull(document); // Сохраняем полный объект для sidebar
    setEditingDocumentNumber(getDocumentNumber(document));
    setEditingDocumentStatus(document.status || null);
    setDocumentType(document.type);

    // Восстановление режима маркировки для возврата (State = 4)
    // Если RequestMarks = 1 -> маркированный, если 0 или null (старый док) -> без маркировки
    if (document.type === 4 || document.state === 4) {
      const isMarked = document.requestMarks === 1 || document.isMarkedProduct === true;
      setIsMarkedProduct(isMarked);
    } else {
      setIsMarkedProduct(false);
    }

    // Настройка получателя в зависимости от типа документа
    if (document.type === 4) {
      editingPharmacyResetRef.current = false;
      setToPharmacy(null);
      setPharmacySearchInput('');
      setPendingPharmacyNum(null);
      setFilteredPharmacies([]);

      // Загружаем список складов
      fetchStorages();

      const storageInfo = {
        guid: document.stockIdIn || null,
        name: document.toPharmacy || null
      };
      setPendingStorageInfo(storageInfo);

      // Если склады уже загружены в памяти, сопоставляем немедленно
      if (storages.length > 0) {
        const targetStorage = storages.find(s => {
          const matchGuid = storageInfo.guid && s.guid && s.guid.toLowerCase() === storageInfo.guid.toLowerCase();
          const matchUsername = storageInfo.name && s.username && s.username.toLowerCase() === storageInfo.name.toLowerCase();
          const matchPharmacyNum = storageInfo.name && s.pharmacyNum && s.pharmacyNum.toLowerCase() === storageInfo.name.toLowerCase();
          return matchGuid || matchUsername || matchPharmacyNum;
        });

        if (targetStorage) {
          setToStorage(targetStorage);
          setStorageSearchInput(targetStorage.username);
          setPendingStorageInfo(null);
        } else if (storageInfo.name || storageInfo.guid) {
          const fallback: Storage = {
            id: 0,
            username: storageInfo.name || 'Склад',
            pharmacyNum: storageInfo.name || '',
            guid: storageInfo.guid || ''
          };
          setToStorage(fallback);
          setStorageSearchInput(fallback.username);
          setPendingStorageInfo(null);
        }
      }
    } else if (document.type === 5) {
      setToStorage(null);
      setStorageSearchInput('');
      setFilteredStorages([]);
      setPendingStorageInfo(null);

      // Настройка аптек - используем pendingPharmacyNum + ref чтобы throughRC-эффект
      // не сбросил получателя и загрузил полный список аптек
      editingPharmacyResetRef.current = true;
      if (document.toPharmacy) {
        const pharmacyNum = document.toPharmacy.replace(/^Аптека №\s*/, '').trim();
        //console.log(' Отложенная загрузка аптеки-получателя:', pharmacyNum);
        setPendingPharmacyNum(pharmacyNum);
        setToPharmacy(null);
        setPharmacySearchInput('');
      } else {
        setToPharmacy(null);
        setPharmacySearchInput('');
        setPendingPharmacyNum(null);
      }
    } else {
      setToPharmacy(null);
      setPharmacySearchInput('');
      setPendingPharmacyNum(null);
      setToStorage(null);
      setStorageSearchInput('');
      setFilteredStorages([]);
      setPendingStorageInfo(null);
      editingPharmacyResetRef.current = false;
    }

    // Настройка TypeMovement -> ThroughRC
    //console.log(' TypeMovement check:', { typeMovement: document.typeMovement, doc: document });
    if (document.typeMovement === 2) {
      setThroughRC(true);
    } else if (document.typeMovement === 1) {
      setThroughRC(false);
    } else {
      setThroughRC(null); // Для старых документов без typeMovement - оставляем пустым
    }

    // Загружаем комментарий
    setComment(document.comment || '');

    // Загружаем boxCount (если есть в документе)
    setBoxCount(document.boxCount || 0);

    // Загружаем данные товаров
    loadDocumentData(document);
    setOpenDialog(true);
  };

  // Открытие/загрузка документа при клике
  const handleViewDocument = useCallback(async (document: Document) => {
    if (isLoadingDocument) return;

    setIsLoadingDocument(true);
    setSelectedDocument(document);

    // Получаем роль пользователя
    const userRole = localStorage.getItem('role');

    try {
      // Для черновиков/сканирования запрашиваем полные (немаскированные) КМ-коды
      const isEditable = !document.isIncoming && (document.status === 9 || document.status === 12);
      const response = await fetchDocument(document.id, isEditable ? 'edit' : undefined);

      if (response.success && response.document) {
        // Сохраняем флаги isIncoming и isReturnIncoming из списка, так как детальный endpoint их не возвращает
        const isReturn = Boolean(
          document.isReturnIncoming ||
          document.state === 4 ||
          document.type === 4 ||
          response.document?.state === 4 ||
          response.document?.type === 4
        );
        const loadedDocument = {
          ...response.document,
          isIncoming: document.isIncoming,
          isReturnIncoming: isReturn
        };

        // 1. Для складов (role = storage) + входящий возврат + статус ожидает подтверждения - стандартная приёмка
        if (userRole === 'storage' && loadedDocument.isReturnIncoming && (loadedDocument.status === 15 || loadedDocument.status === 6)) {
          setReceivingDocument(loadedDocument);
          setReceivingDialogOpen(true);
        }
        // 1b. Для складов (role = storage) + транзитный документ + статус ожидает подтверждения - только короба
        else if (userRole === 'storage' && loadedDocument.isIncoming && (loadedDocument.status === 15 || loadedDocument.status === 6)) {
          setReceivingDocument(loadedDocument);
          setReceivingDialogOpen(true);
        }
        // 2. Для исходящих документов со статусом "Черновик" (9) или "Сканирование коробов" (12) - редактирование
        else if (!loadedDocument.isIncoming && (loadedDocument.status === 9 || loadedDocument.status === 12)) {
          handleEditDocument(loadedDocument);
        }
        // 3. Для входящих документов со статусом 15 (Ожидает подтверждения) - диалог подтверждения с mode=pharmacy
        else if (loadedDocument.isIncoming && loadedDocument.status === 15) {
          setReceivingDocument(loadedDocument);
          setReceivingDialogOpen(true);
        }
        // 4. Для всех остальных (принятые, отклоненные, в пути, переданные и т.д.) - просмотр
        else {
          setSelectedDocument(loadedDocument);
          setViewDialogOpen(true);
        }
      } else {
        notify.error('Ошибка при загрузке документа');
      }
    } catch (error) {
      console.error('Ошибка при загрузке документа:', error);
      notify.error('Ошибка при загрузке документа');
    } finally {
      setIsLoadingDocument(false);
    }
  }, [isLoadingDocument, fetchDocument]);

  // Функции для меню действий
  const handleMenuClick = useCallback((event: React.MouseEvent<HTMLElement>, document: Document) => {
    event.stopPropagation();
    event.preventDefault(); // Предотвращаем стандартное поведение
    setAnchorPosition({
      top: event.clientY,
      left: event.clientX
    });
    setSelectedDocumentForMenu(document);
  }, []);

  const handleMenuClose = () => {
    setAnchorPosition(null);
    setSelectedDocumentForMenu(null);
  };

  const openDeleteDialog = (document: Document) => {
    setDocumentToDelete(document);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteDocument = async () => {
    if (!documentToDelete || isDeleting) return;

    setIsDeleting(true);
    try {
      const result = await deleteDocument(documentToDelete.id, username);

      if (result.success) {
        notify.success(result.message!);
        await reloadDocuments();
      } else {
        notify.error(result.message!);
      }
    } catch (error) {
      console.error('Ошибка при удалении документа:', error);
      notify.error('Ошибка при удалении документа');
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
    }
  };

  // Создание нового документа на основании документа с ошибкой
  const handleCreateFromDocument = async (document: Document) => {
    // Закрываем диалог просмотра если открыт
    setViewDialogOpen(false);
    handleMenuClose();

    try {
      // Если tableData не загружен (документ из списка), загружаем его
      let documentWithData = document;
      if (!document.tableData || document.tableData.length === 0) {
        notify.info('Загрузка данных документа...');
        const result = await fetchDocument(document.id);
        if (result.success && result.document) {
          documentWithData = result.document;
        } else {
          notify.error('Ошибка загрузки документа');
          return;
        }
      }

      // Устанавливаем флаг чтобы пропустить восстановление автосохранения
      skipAutosaveRestoreRef.current = true;

      // Очищаем автосохранение ПЕРЕД открытием диалога
      localStorage.removeItem(AUTOSAVE_KEY);

      // Открываем диалог создания с предзаполненными данными
      setOpenDialog(true);
      setIsEditMode(false); // Это НОВЫЙ документ
      setEditingDocumentId(null);
      setEditingDocumentFull(null);
      setEditingDocumentNumber(undefined);
      setEditingDocumentStatus(null);

      // Копируем тип документа
      setDocumentType(documentWithData.type);

      // Восстановление режима маркировки для возвратов (State = 4)
      if (documentWithData.type === 4 || documentWithData.state === 4) {
        const isMarked = documentWithData.requestMarks === 1 || documentWithData.isMarkedProduct === true;
        setIsMarkedProduct(isMarked);
      } else {
        setIsMarkedProduct(false);
      }

      // throughRC - оставляем как в форме по умолчанию (false)
      setThroughRC(false);

      // НЕ копируем: boxCount, comment
      setBoxCount(0);
      setComment('');

      // Копируем получателя для склада (возвраты)
      if (documentWithData.type === 4 && documentWithData.toPharmacy) {
        fetchStorages();
        const storageInfo = {
          guid: documentWithData.stockIdIn || null,
          name: documentWithData.toPharmacy || null
        };
        setPendingStorageInfo(storageInfo);
        if (storages.length > 0) {
          const targetStorage = storages.find(s => {
            const matchGuid = storageInfo.guid && s.guid && s.guid.toLowerCase() === storageInfo.guid.toLowerCase();
            const matchUsername = storageInfo.name && s.username && s.username.toLowerCase() === storageInfo.name.toLowerCase();
            const matchPharmacyNum = storageInfo.name && s.pharmacyNum && s.pharmacyNum.toLowerCase() === storageInfo.name.toLowerCase();
            return matchGuid || matchUsername || matchPharmacyNum;
          });
          if (targetStorage) {
            setToStorage(targetStorage);
            setStorageSearchInput(targetStorage.username);
            setPendingStorageInfo(null);
          }
        }
      }

      // Копируем аптеку получателя (для перемещений)
      if (documentWithData.type === 5 && documentWithData.toPharmacy) {
        const targetPharmacyNum = documentWithData.toPharmacy.replace(/^Аптека №\s*/, '').trim();
        const targetPharmacy = users.find(u => String(u.pharmacyNum).trim() === targetPharmacyNum);
        if (targetPharmacy) {
          setToPharmacy({
            pharmacyNum: targetPharmacy.pharmacyNum,
            guid: targetPharmacy.guid,
            username: targetPharmacy.username,
            belongsStorage: targetPharmacy.belongsStorage,
            cityId: targetPharmacy.cityId
          });
          setPharmacySearchInput(`Аптека №${targetPharmacy.pharmacyNum}`);
        } else {
          // Если аптека не найдена, сохраняем для отложенной загрузки
          setPendingPharmacyNum(targetPharmacyNum);
          setToPharmacy(null);
          setPharmacySearchInput('');
        }
      } else {
        setToPharmacy(null);
        setPharmacySearchInput('');
      }

      // Копируем товары БЕЗ маркировочных кодов
      if (documentWithData.tableData && documentWithData.tableData.length > 0) {
        const copiedTableData = documentWithData.tableData.map(row => ({
          id: Date.now().toString() + Math.random().toString().slice(2),
          MaterialZid: row.MaterialZid || '',
          MaterialCode: row.MaterialCode || '',
          MaterialName: row.MaterialName || '',
          SerialNumber: row.SerialNumber || '',
          SerialDate: row.SerialDate || '',
          Quantity: row.Quantity || '',
          QuantityPcs: row.QuantityPcs || '',
          Quantity1C: '', // Сбрасываем
          Barcode: row.Barcode || '',
          Comment: '', // Сбрасываем комментарий к товару
          markCodes: [] as string[], // НЕ копируем маркировочные коды
          partialScanning: false,
          selectedMaterial: row.MaterialName ? {
            ProductZid: row.MaterialZid || '',
            ProductSku: row.MaterialCode || '',
            ProductName: row.MaterialName,
            Barcode: row.Barcode || '',
            PartExists: !!row.PartExists,
            PartCoeff: row.PartCoeff || 0
          } : undefined
        }));
        setTableData(copiedTableData);

        // Устанавливаем materialSearchInputs для корректного отображения в форме
        const searchInputs: { [key: string]: string } = {};
        copiedTableData.forEach(row => {
          if (row.MaterialName) {
            searchInputs[row.id] = row.MaterialName;
          }
        });
        setMaterialSearchInputs(searchInputs);
      } else {
        setTableData([createEmptyRow()]);
        setMaterialSearchInputs({});
      }

      notify.info('Создание нового документа на основании');
    } catch (error) {
      console.error('Ошибка при создании документа на основании:', error);
      notify.error('Ошибка при создании документа');
    }
  };

  // Сброс всех фильтров
  const resetFilters = () => {
    setFilterType('all');
    setFilterStatus('all');
    setFilterDirection('all');
    setSortBy('id');
    setSortOrder('desc');
    setDocumentSearch('');
  };

  // Получение статистики и отфильтрованных документов (без пагинации)
  const stats = getDocumentsStats(documents, incomingDocuments);
  const filteredByParams = getAllFilteredDocuments(
    documents,
    incomingDocuments,
    filterType,
    filterStatus,
    filterDirection,
    sortBy,
    sortOrder,
    username
  );

  // Поиск по уже загруженному списку: номер документа, ID, 1С, аптеки, тип, комментарий, штрихкод
  const searchTerm = documentSearch.trim().toLowerCase();
  const filteredDocuments = searchTerm
    ? filteredByParams.filter(doc => {
      const docNum = String(getDocumentNumber(doc));
      const docId = String(doc.id || '');
      const movIn = String(doc.movementIdIn || '');
      const movOut = String(doc.movementIdOut || '');
      const fromP = String(doc.fromPharmacy || '');
      const toP = String(doc.toPharmacy || '');
      const typeName = String(doc.typeName || '');
      const comment = String(doc.comment || '');

      // Быстрая проверка основных реквизитов документа
      if (
        docNum.includes(searchTerm) ||
        docId.toLowerCase().includes(searchTerm) ||
        movIn.toLowerCase().includes(searchTerm) ||
        movOut.toLowerCase().includes(searchTerm) ||
        fromP.toLowerCase().includes(searchTerm) ||
        toP.toLowerCase().includes(searchTerm) ||
        typeName.toLowerCase().includes(searchTerm) ||
        comment.toLowerCase().includes(searchTerm)
      ) {
        return true;
      }

      // Поиск по содержимому (штрихкоды, наименования, коды материалов, если загружены)
      if (doc.tableData && doc.tableData.length > 0) {
        return doc.tableData.some(row =>
          String(row.Barcode || '').toLowerCase().includes(searchTerm) ||
          String(row.MaterialCode || '').toLowerCase().includes(searchTerm) ||
          String(row.MaterialName || '').toLowerCase().includes(searchTerm) ||
          String(row.SerialNumber || '').toLowerCase().includes(searchTerm)
        );
      }

      return false;
    })
    : filteredByParams;

  // Проверяем роль пользователя
  const userRole = localStorage.getItem('role');

  const canCreateDocument = !!(userRole !== 'storage');
  return (
    <Box sx={{ maxHeight: '100%', position: 'relative' }}>
      {/* CapsLock Overlay */}
      <CapsLockOverlay />
      <Grid container spacing={2} sx={{ height: '100%', pb: 2, position: 'relative' }}>


        {/* Управление push-уведомлениями */}
        <Box sx={{ position: 'absolute', right: 0, bottom: 15, zIndex: 1000 }}>
          <PushNotificationManager />
        </Box>
        {/* Фильтры и статистика */}
        {!isLoading && (
          <Grid item xs={12}>
            <DocumentFilters
              username={username}
              pharmacyGuid={guid}
              filterType={filterType}
              filterStatus={filterStatus}
              filterDirection={filterDirection}
              sortBy={sortBy}
              sortOrder={sortOrder}
              stats={stats}
              onFilterTypeChange={setFilterType}
              onFilterStatusChange={setFilterStatus}
              onFilterDirectionChange={setFilterDirection}
              onSortByChange={setSortBy}
              onSortOrderChange={setSortOrder}
              onResetFilters={resetFilters}
              onRefresh={reloadDocuments}
              onCreateDocument={handleCreateDocument}
              canCreateDocument={canCreateDocument}
              isLoading={isLoading}
              searchQuery={documentSearch}
              onSearchChange={setDocumentSearch}
            />
          </Grid>
        )}

        {/* Список документов */}
        <Grid item xs={12} sx={{ flexGrow: 1, height: '100%', overflow: 'hidden' }}>
          <DocumentList
            documents={filteredDocuments}
            incomingDocuments={incomingDocuments}
            isLoading={isLoading}
            isLoadingDocument={isLoadingDocument}
            currentUsername={username}
            onDocumentClick={handleViewDocument}
            onMenuClick={handleMenuClick}
            highlightedDocumentId={highlightedDocumentId}
          />
        </Grid>

        {/* Диалог создания/редактирования документа */}
        <DocumentDialog
          open={openDialog}
          isEditMode={isEditMode}
          documentType={documentType}
          documentNumber={editingDocumentNumber}
          documentStatus={editingDocumentStatus}
          editingDocument={editingDocumentFull}
          toPharmacy={toPharmacy}
          pharmacyNum={pharmacyNum}
          tableData={tableData}
          users={users}
          materials={materials}
          materialSearchInputs={materialSearchInputs}
          pharmacySearchInput={pharmacySearchInput}
          filteredPharmacies={filteredPharmacies}
          isSaving={isSaving}
          throughRC={throughRC}
          boxCount={boxCount}
          comment={comment}
          isMarkedProduct={isMarkedProduct}
          onClose={handleBackdropClick}
          onCancel={handleCancelClick}
          onDocumentTypeChange={(newType: number) => {
            if (newType === documentType) return;
            const hasItems = tableData.some(row => !!row.MaterialName);

            if (newType === 4) {
              // Переход на Возврат: всегда запрашиваем параметр маркировки
              setPendingDocumentType(4);
              setChangeTypeDialogOpen(true);
            } else if (newType === 5) {
              // Переход на Перемещение: если есть товары, предупреждаем об очистке
              if (hasItems) {
                setPendingDocumentType(5);
                setChangeTypeDialogOpen(true);
              } else {
                setDocumentType(5);
                setIsMarkedProduct(false);
              }
            } else {
              setDocumentType(newType);
            }
          }}
          onPharmacySearchChange={handlePharmacySearchChange}
          onPharmacySelect={handlePharmacySelect}
          storages={storages}
          toStorage={toStorage}
          storageSearchInput={storageSearchInput}
          filteredStorages={filteredStorages}
          onStorageSearchChange={handleStorageSearchChange}
          onStorageSelect={handleStorageSelect}
          onAddTableRow={addTableRow}
          onRemoveTableRow={removeTableRow}
          onUpdateTableRow={updateTableRow}
          onMaterialSearchChange={handleMaterialSearchChangeWrapper}
          onMaterialSelect={handleMaterialSelect}
          onSaveDocument={handleSaveDocument}
          onSaveAsDraft={handleSaveAsDraft}
          onCompleteWithBoxes={handleCompleteWithBoxes}
          onThroughRCChange={setThroughRC}
          onBoxCountChange={setBoxCount}
          onCommentChange={setComment}
        />

        {/* Диалог просмотра документа */}
        <DocumentViewDialog
          open={viewDialogOpen}
          document={selectedDocument}
          onClose={() => setViewDialogOpen(false)}
          onCreateFromDocument={handleCreateFromDocument}
        />

        {/* Диалог подтверждения получения */}
        <ReceivingConfirmationDialog
          open={receivingDialogOpen}
          document={receivingDocument}
          mode={
            userRole === 'storage'
              ? ((receivingDocument?.isReturnIncoming || receivingDocument?.state === 4 || receivingDocument?.type === 4) ? 'storage-return' : 'storage')
              : 'pharmacy'
          }
          onClose={() => {
            setReceivingDialogOpen(false);
            setReceivingDocument(null);
          }}
          onConfirm={async () => {
            try {
              await reloadDocuments();
              const isStorageReturnDoc = Boolean(receivingDocument?.isReturnIncoming || receivingDocument?.state === 4 || receivingDocument?.type === 4);
              const message = (userRole === 'storage' && !isStorageReturnDoc)
                ? 'Документ успешно передан на следующую точку'
                : 'Документ успешно принят';
              notify.success(message);
            } catch (error) {
              console.error('Ошибка при обновлении списка:', error);
            }
          }}
        />


        {/* Диалог подтверждения смены типа документа и выбора маркировки */}
        <Dialog
          open={changeTypeDialogOpen}
          onClose={() => {
            setChangeTypeDialogOpen(false);
            setPendingDocumentType(null);
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2, maxWidth: 520 }
          }}
        >
          {pendingDocumentType === 4 ? (
            <>
              <DialogTitle sx={{ fontWeight: 700, pb: 1, fontSize: '1.25rem' }}>
                {tableData.some(row => !!row.MaterialName)
                  ? 'Сменить тип на «Возврат»?'
                  : 'Параметры возврата'}
              </DialogTitle>
              <DialogContent sx={{ pb: 1 }}>
                {tableData.some(row => !!row.MaterialName) && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Вы уже добавили товары в документ. При смене типа список товаров будет очищен,
                    так как для разных типов используются разные каталоги.
                  </Alert>
                )}
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.75, color: '#1a1a1a' }}>
                  Маркированный товар?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  Укажите, содержит ли возврат маркированную продукцию. При выборе <strong>«Да»</strong> в документе будут строго требоваться коды маркировки для всех товаров, а опция «Не требовать все коды» будет скрыта.
                </Typography>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                  onClick={() => {
                    setChangeTypeDialogOpen(false);
                    setPendingDocumentType(null);
                  }}
                  color="inherit"
                  sx={{ textTransform: 'none', color: 'text.secondary' }}
                >
                  Отмена
                </Button>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Button
                    onClick={() => {
                      setDocumentType(4);
                      setIsMarkedProduct(false);
                      setTableData([createEmptyRow()]);
                      setMaterialSearchInputs({});
                      setToPharmacy(null);
                      setToStorage(null);
                      setPharmacySearchInput('');
                      setStorageSearchInput('');
                      setPendingStorageInfo(null);
                      setPendingPharmacyNum(null);
                      setBoxCount(0);
                      setComment('');
                      localStorage.removeItem(AUTOSAVE_KEY);
                      setChangeTypeDialogOpen(false);
                      setPendingDocumentType(null);
                    }}
                    variant="outlined"
                    color="inherit"
                    sx={{ textTransform: 'none', fontWeight: 500 }}
                  >
                    Нет (без маркировки)
                  </Button>
                  <Button
                    onClick={() => {
                      setDocumentType(4);
                      setIsMarkedProduct(true);
                      setTableData([createEmptyRow()]);
                      setMaterialSearchInputs({});
                      setToPharmacy(null);
                      setToStorage(null);
                      setPharmacySearchInput('');
                      setStorageSearchInput('');
                      setPendingStorageInfo(null);
                      setPendingPharmacyNum(null);
                      setBoxCount(0);
                      setComment('');
                      localStorage.removeItem(AUTOSAVE_KEY);
                      setChangeTypeDialogOpen(false);
                      setPendingDocumentType(null);
                    }}
                    variant="contained"
                    color="primary"
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Да (маркированный)
                  </Button>
                </Box>
              </DialogActions>
            </>
          ) : (
            <>
              <DialogTitle sx={{ fontWeight: 700, pb: 1, fontSize: '1.25rem' }}>Сменить тип документа?</DialogTitle>
              <DialogContent sx={{ pb: 1 }}>
                <DialogContentText sx={{ lineHeight: 1.5 }}>
                  Вы уже добавили товары в документ. При смене типа список товаров будет очищен,
                  так как для разных типов используются разные каталоги.
                </DialogContentText>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                <Button
                  onClick={() => {
                    setChangeTypeDialogOpen(false);
                    setPendingDocumentType(null);
                  }}
                  color="inherit"
                  sx={{ textTransform: 'none', color: 'text.secondary' }}
                >
                  Отмена
                </Button>
                <Button
                  onClick={() => {
                    if (pendingDocumentType !== null) {
                      setDocumentType(pendingDocumentType);
                      setIsMarkedProduct(false);
                      setTableData([createEmptyRow()]);
                      setMaterialSearchInputs({});
                      setToPharmacy(null);
                      setToStorage(null);
                      setPharmacySearchInput('');
                      setStorageSearchInput('');
                      setPendingStorageInfo(null);
                      setPendingPharmacyNum(null);
                      setBoxCount(0);
                      setComment('');
                      localStorage.removeItem(AUTOSAVE_KEY);
                    }
                    setChangeTypeDialogOpen(false);
                    setPendingDocumentType(null);
                  }}
                  variant="contained"
                  color="error"
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Сменить тип и очистить
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>


        {/* Меню действий и диалог удаления */}
        <DocumentActions
          anchorPosition={anchorPosition}
          selectedDocument={selectedDocumentForMenu}
          deleteDialogOpen={deleteDialogOpen}
          documentToDelete={documentToDelete}
          isDeleting={isDeleting}
          onMenuClose={handleMenuClose}
          onOpenDeleteDialog={openDeleteDialog}
          onCloseDeleteDialog={() => setDeleteDialogOpen(false)}
          onConfirmDelete={handleDeleteDocument}
          onCreateFromDocument={handleCreateFromDocument}
        />
      </Grid>
    </Box>
  );
};

export default Documents;